import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Json } from '@/integrations/supabase/types';

export interface EngagementPoint {
  id: string;
  brand_id: string;
  user_id: string;
  product_id: string | null;
  business_stage_id: string | null;
  persona_ids: string[];
  page_name: string;
  route: string;
  form_name: string | null;
  form_fields: string[];
  webhook_url: string | null;
  webhook_enabled: boolean;
  webhook_config: Record<string, unknown>;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Joined product data
  product?: {
    id: string;
    product: string;
    type: string;
  } | null;
  // Joined business stage data
  business_stage?: {
    id: string;
    name: string;
    color: string;
  } | null;
}

interface CreateEngagementPointInput {
  brand_id: string;
  user_id: string;
  product_id?: string | null;
  business_stage_id?: string | null;
  persona_ids?: string[];
  page_name: string;
  route: string;
  form_name?: string | null;
  form_fields?: string[];
  webhook_url?: string | null;
  webhook_enabled?: boolean;
  webhook_config?: Record<string, unknown>;
  description?: string | null;
  is_active?: boolean;
}

interface UpdateEngagementPointInput {
  id: string;
  product_id?: string | null;
  business_stage_id?: string | null;
  persona_ids?: string[];
  page_name?: string;
  route?: string;
  form_name?: string | null;
  form_fields?: string[];
  webhook_url?: string | null;
  webhook_enabled?: boolean;
  webhook_config?: Record<string, unknown>;
  description?: string | null;
  is_active?: boolean;
}

const parseJsonArray = (value: Json | null | undefined): string[] => {
  if (Array.isArray(value)) {
    return value.map(item => String(item));
  }
  return [];
};

const parseJsonObject = (value: Json | null | undefined): Record<string, unknown> => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
};

export const useEngagementPoints = (brandId: string) => {
  const queryClient = useQueryClient();

  const fetchEngagementPoints = async (): Promise<EngagementPoint[]> => {
    const { data, error } = await supabase
      .from('os_engagement_points')
      .select(`
        *,
        product:brand_products_hooks(id, product, type),
        business_stage:os_business_stages(id, name, color)
      `)
      .eq('brand_id', brandId)
      .order('created_at', { ascending: false });

    if (error) {
      throw error;
    }

    return (data || []).map(item => ({
      id: item.id,
      brand_id: item.brand_id,
      user_id: item.user_id,
      product_id: item.product_id,
      business_stage_id: item.business_stage_id,
      persona_ids: parseJsonArray((item as any).persona_ids),
      page_name: item.page_name,
      route: item.route,
      form_name: item.form_name,
      form_fields: parseJsonArray(item.form_fields),
      webhook_url: item.webhook_url,
      webhook_enabled: item.webhook_enabled ?? false,
      webhook_config: parseJsonObject(item.webhook_config),
      description: item.description,
      is_active: item.is_active ?? true,
      created_at: item.created_at,
      updated_at: item.updated_at,
      product: item.product,
      business_stage: item.business_stage,
    }));
  };

  const { data: engagementPoints = [], isLoading, error } = useQuery({
    queryKey: ['engagement-points', brandId],
    queryFn: fetchEngagementPoints,
    enabled: !!brandId,
  });

  const createEngagementPoint = useMutation({
    mutationFn: async (input: CreateEngagementPointInput) => {
      const { data, error } = await supabase
        .from('os_engagement_points')
        .insert({
          brand_id: input.brand_id,
          user_id: input.user_id,
          product_id: input.product_id || null,
          business_stage_id: input.business_stage_id || null,
          persona_ids: (input.persona_ids || []) as unknown as Json,
          page_name: input.page_name,
          route: input.route,
          form_name: input.form_name || null,
          form_fields: (input.form_fields || []) as unknown as Json,
          webhook_url: input.webhook_url || null,
          webhook_enabled: input.webhook_enabled ?? false,
          webhook_config: (input.webhook_config || {}) as unknown as Json,
          description: input.description || null,
          is_active: input.is_active ?? true,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['engagement-points', brandId] });
      toast.success('Engagement point created successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create engagement point: ${error.message}`);
    },
  });

  const updateEngagementPoint = useMutation({
    mutationFn: async (input: UpdateEngagementPointInput) => {
      const { id, ...rest } = input;
      
      // Build update object with proper type casting
      const updateData: Record<string, unknown> = {};
      if (rest.product_id !== undefined) updateData.product_id = rest.product_id;
      if (rest.business_stage_id !== undefined) updateData.business_stage_id = rest.business_stage_id;
      if (rest.persona_ids !== undefined) updateData.persona_ids = rest.persona_ids as unknown as Json;
      if (rest.page_name !== undefined) updateData.page_name = rest.page_name;
      if (rest.route !== undefined) updateData.route = rest.route;
      if (rest.form_name !== undefined) updateData.form_name = rest.form_name;
      if (rest.form_fields !== undefined) updateData.form_fields = rest.form_fields as unknown as Json;
      if (rest.webhook_url !== undefined) updateData.webhook_url = rest.webhook_url;
      if (rest.webhook_enabled !== undefined) updateData.webhook_enabled = rest.webhook_enabled;
      if (rest.webhook_config !== undefined) updateData.webhook_config = rest.webhook_config as unknown as Json;
      if (rest.description !== undefined) updateData.description = rest.description;
      if (rest.is_active !== undefined) updateData.is_active = rest.is_active;

      const { data, error } = await supabase
        .from('os_engagement_points')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['engagement-points', brandId] });
      toast.success('Engagement point updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update engagement point: ${error.message}`);
    },
  });

  const deleteEngagementPoint = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('os_engagement_points')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['engagement-points', brandId] });
      toast.success('Engagement point deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete engagement point: ${error.message}`);
    },
  });

  return {
    engagementPoints,
    isLoading,
    error,
    createEngagementPoint,
    updateEngagementPoint,
    deleteEngagementPoint,
  };
};
